import { Link, NavLink, useNavigate, useLocation } from "react-router-dom";
import { Home, Music2, Disc3, FolderKanban, BarChart3, Archive, User, Settings, Plus, LogOut, Sparkles, Globe, Library } from "lucide-react";
import { MobileHeader } from "@/components/MobileHeader";
import { useAuth } from "@/context/AuthContext";
import { usePlayer } from "@/context/PlayerContext";
import { MiniPlayer } from "@/components/MiniPlayer";
import { FullPlayer } from "@/components/FullPlayer";
import { AppMark } from "@/components/AppMark";
import { ActiveSessionBanner } from "@/components/ActiveSessionBanner";
import { EnvBadge } from "@/components/EnvBadge";
import { useState } from "react";
import { NewItemDialog } from "@/components/NewItemDialog";

const navItems = [
  { to: "/", label: "Home", icon: Home, end: true, testid: "nav-home" },
  { to: "/songs", label: "Songs", icon: Music2, testid: "nav-songs" },
  { to: "/beats", label: "Beats", icon: Disc3, testid: "nav-beats" },
  { to: "/projects", label: "Projects", icon: FolderKanban, testid: "nav-projects" },
  { to: "/catalog", label: "Catalog", icon: Library, testid: "nav-catalog" },
  { to: "/releases", label: "Releases", icon: Sparkles, testid: "nav-releases" },
  { to: "/wrld", label: "LXN WRLD", icon: Globe, testid: "nav-wrld" },
  { to: "/statistics", label: "Statistics", icon: BarChart3, testid: "nav-statistics" },
  { to: "/archive", label: "Archive", icon: Archive, testid: "nav-archive" },
];
const lowerItems = [
  { to: "/account", label: "Account", icon: User, testid: "nav-account" },
  { to: "/settings", label: "Settings", icon: Settings, testid: "nav-settings" },
];

export function Layout({ children }) {
  const { user, logout } = useAuth();
  const { current, fullPlayerOpen } = usePlayer();
  const [openNew, setOpenNew] = useState(false);

  return (
    <div className="hq-root min-h-[100dvh] bg-[#0A0A0C] text-white overflow-x-hidden relative" style={{ paddingTop: "env(safe-area-inset-top, 0px)", paddingLeft: "env(safe-area-inset-left, 0px)", paddingRight: "env(safe-area-inset-right, 0px)" }}>
      {/* HQ subtle fixed background — same linework language as lxnwrld.com.
          Fixed to the viewport, does not affect layout, does not scroll. */}
      <div aria-hidden="true" className="fixed inset-0 z-0 pointer-events-none overflow-hidden">
        <svg width="0" height="0" style={{ position: "absolute" }}>
          <defs>
            <pattern id="hq-lxn-lines" x="0" y="0" width="220" height="220" patternUnits="userSpaceOnUse" patternTransform="rotate(-8)">
              <path d="M20 200 L120 20" stroke="#141418" strokeWidth="0.6" fill="none" />
              <path d="M60 200 L200 40" stroke="#101014" strokeWidth="0.5" fill="none" />
              <path d="M40 80 L180 210" stroke="#0f0f12" strokeWidth="0.5" fill="none" />
              <path d="M110 30 L115 190" stroke="#111114" strokeWidth="0.4" fill="none" />
              <path d="M150 60 L100 120 L170 130" stroke="#131316" strokeWidth="0.4" fill="none" />
            </pattern>
          </defs>
        </svg>
        <svg className="absolute inset-0 w-full h-full" preserveAspectRatio="none">
          <rect width="100%" height="100%" fill="url(#hq-lxn-lines)" />
        </svg>
      </div>
      <div className="relative z-10">
      {/* Desktop sidebar */}
      <aside className="hidden md:flex fixed left-0 top-0 bottom-0 w-64 bg-[#0C0C0E] border-r border-white/5 flex-col z-30">
        <div className="px-6 py-6">
          <Link to="/" className="flex items-center gap-3" data-testid="brand-link">
            <div className="w-9 h-9 rounded-xl bg-[#141416] border border-white/10 flex items-center justify-center text-violet-400">
              <AppMark size={18} />
            </div>
            <div>
              <div className="text-sm font-bold tracking-widest">LXN HQ</div>
              <div className="text-[9px] tracking-[0.5em] text-zinc-600 uppercase mono mt-1" data-testid="sidebar-est">EST. 2026</div>
            </div>
          </Link>
        </div>
        <button
          onClick={() => setOpenNew(true)}
          className="mx-4 mb-4 flex items-center gap-2 px-4 py-2.5 rounded-full bg-violet-600 hover:bg-violet-500 text-white font-medium transition-colors text-sm"
          data-testid="sidebar-new-btn"
        >
          <Plus className="w-4 h-4" /> New
        </button>
        <nav className="flex-1 px-3 space-y-0.5 overflow-y-auto">
          {navItems.map((it) => (
            <NavLink
              key={it.to} to={it.to} end={it.end}
              data-testid={it.testid}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${
                  isActive ? "bg-white/5 text-white border border-white/5" : "text-zinc-400 hover:text-white hover:bg-white/[0.03]"
                }`
              }
            >
              <it.icon className="w-4 h-4" /> <span>{it.label}</span>
            </NavLink>
          ))}
        </nav>
        <div className="px-3 pt-3 border-t border-white/5 space-y-0.5 pb-4">
          {lowerItems.map((it) => (
            <NavLink
              key={it.to} to={it.to}
              data-testid={it.testid}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${
                  isActive ? "bg-white/5 text-white" : "text-zinc-400 hover:text-white hover:bg-white/[0.03]"
                }`
              }
            >
              <it.icon className="w-4 h-4" /> <span>{it.label}</span>
            </NavLink>
          ))}
          <button onClick={logout} data-testid="logout-btn"
            className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-zinc-500 hover:text-white hover:bg-white/[0.03] w-full">
            <LogOut className="w-4 h-4" /> <span>Log out</span>
          </button>
          <div className="mt-2 px-3 py-2 text-xs">
            <div className="text-zinc-300 truncate">{user?.display_name}</div>
            <div className="text-zinc-500 truncate">{user?.email}</div>
            <div className="text-[10px] mt-1 text-violet-400 uppercase tracking-widest">{user?.role === "owner" ? "Owner" : "Guest"}</div>
          </div>
        </div>
      </aside>

      <MobileHeader hidden={fullPlayerOpen} />

      <main className={`md:ml-64 min-h-screen pt-14 md:pt-0 ${current ? "pb-28 md:pb-32" : "pb-10 md:pb-28"} ${fullPlayerOpen ? "overflow-hidden" : ""}`}>
        {children}
      </main>

      <MiniPlayer hidden={fullPlayerOpen} />
      <ActiveSessionBanner />
      <EnvBadge />

      <FullPlayer />

      {/* Phase 6.5 — floating mobile `+` moved to the Home header. Keeping
          this block removed so there is no duplicate Add action anywhere. */}

      <NewItemDialog open={openNew} onOpenChange={setOpenNew} />
      </div>
    </div>
  );
}
